/**
 * ProfileNudgeBanner — first-visit reminder to complete the Investor Profile.
 *
 * Renders a dismissible banner at the top of the deal page when:
 *   - The user has NO Investor Profile yet, OR
 *   - The user's profile is incomplete (missing the essentials the fit
 *     scorer needs).
 *
 * Dismissal is persisted per-user in localStorage so we never nag on repeat
 * visits. Once the profile is completed, the banner disappears automatically.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { Sparkles, ArrowRight, X } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const STORAGE_KEY = 'rp_profile_nudge_dismissed';

function readDismissed() {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}');
  } catch {
    return {};
  }
}

function writeDismissed(map) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(map));
  } catch {
    /* storage unavailable — ignore */
  }
}

export default function ProfileNudgeBanner({ userId }) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [profileState, setProfileState] = useState('loading'); // loading | complete | incomplete | error
  const [dismissed, setDismissed] = useState(false);

  const scopedKey = useMemo(() => userId || 'anon', [userId]);

  // Hydrate dismissal state from storage on mount (and when user changes).
  useEffect(() => {
    const map = readDismissed();
    setDismissed(Boolean(map[scopedKey]));
  }, [scopedKey]);

  useEffect(() => {
    let cancelled = false;
    const lang = i18n.language?.startsWith('fr') ? 'fr' : 'en';
    axios
      .get(`${BACKEND_URL}/api/investor-profile`, {
        withCredentials: true,
        headers: { 'Accept-Language': lang },
      })
      .then((r) => {
        if (cancelled) return;
        setProfileState(r.data?.is_complete ? 'complete' : 'incomplete');
      })
      .catch(() => {
        if (!cancelled) setProfileState('error');
      });
    return () => {
      cancelled = true;
    };
  }, [i18n.language]);

  if (profileState !== 'incomplete' || dismissed) return null;

  const handleDismiss = () => {
    const map = readDismissed();
    map[scopedKey] = Date.now();
    writeDismissed(map);
    setDismissed(true);
  };

  return (
    <div
      data-testid="profile-nudge-banner"
      className="relative flex items-start sm:items-center gap-3 mb-6 rounded-xl border border-[#45C04A]/40 bg-gradient-to-r from-[#45C04A]/10 via-white to-[#12355B]/5 dark:from-[#45C04A]/10 dark:via-[#0F1520] dark:to-[#12355B]/10 px-4 py-3"
    >
      <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-[#45C04A] to-[#65D95A] flex items-center justify-center flex-shrink-0">
        <Sparkles className="w-4 h-4 text-white" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-[#12355B] dark:text-white">
          {t('investorProfile.nudge.title')}
        </p>
        <p className="text-xs text-slate-600 dark:text-slate-300 mt-0.5">
          {t('investorProfile.nudge.subtitle')}
        </p>
      </div>
      <button
        type="button"
        data-testid="profile-nudge-cta"
        onClick={() => navigate('/profile')}
        className="hidden sm:inline-flex items-center gap-1.5 text-sm font-semibold text-white bg-[#12355B] hover:bg-[#1565C0] dark:bg-[#45C04A] dark:hover:bg-[#65D95A] dark:text-[#12355B] rounded-lg px-3 py-1.5 whitespace-nowrap transition-colors"
      >
        {t('investorProfile.nudge.cta')} <ArrowRight className="w-3.5 h-3.5" />
      </button>
      <button
        type="button"
        data-testid="profile-nudge-dismiss"
        aria-label={t('common.close')}
        onClick={handleDismiss}
        className="p-1 rounded-md text-slate-400 hover:text-slate-700 dark:hover:text-white transition-colors"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
}
